import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/auth.service';
import type { User, AuthTokens } from '@trading/shared';
import i18n from '../i18n';
import { usePlaybookStore } from './playbookStore';

// Sync user's language preference with i18n
const syncLanguage = (user: User | null) => {
  if (user?.language && i18n.language !== user.language) {
    i18n.changeLanguage(user.language);
    localStorage.setItem('language', user.language);
  }
};

// Deduplicates concurrent checkAuth calls. Multiple route wrappers
// (ProtectedRoute, PublicRoute, AdminRoute, AuthLayout) each fire checkAuth
// from their own useEffect. Without this guard, concurrent invocations with an
// expired access token race to rotate the refresh token — the loser clears auth
// with an already-consumed token, signing the user out immediately.
let checkAuthInFlight: Promise<void> | null = null;

// Exposed for test isolation; do not call in application code.
export const _resetCheckAuthInFlight = () => {
  checkAuthInFlight = null;
};

interface AuthState {
  user: User | null;
  tokens: AuthTokens | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<{ email: string }>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (email: string) => Promise<void>;
  requestMagicLink: (email: string) => Promise<void>;
  magicLogin: (token: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      login: async (email: string, password: string) => {
        // Do NOT flip global `isLoading` here — see register() above. Login.tsx
        // owns its local submit-loading state. AuthLayout watches `isLoading`
        // for the bootstrap-only check; flipping it during login unmounts the
        // form mid-request and loses the value of any caught error.
        set({ error: null });
        try {
          const tokens = await authService.login(email, password);
          const user = await authService.getMe(tokens.accessToken);
          syncLanguage(user);
          set({
            tokens,
            user,
            isAuthenticated: true,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
          });
          throw error;
        }
      },

      register: async (email: string, password: string, name: string) => {
        // Do NOT touch `isLoading` here — that flag gates AuthLayout's full-screen
        // spinner (used by checkAuth bootstrap). Flipping it during a register
        // call unmounts the form mid-request, so when the 409 catch lands the
        // page sees a fresh form and the existing-account banner has nothing
        // to render against. Register.tsx owns its own local `loading` state
        // for the submit button.
        set({ error: null });
        try {
          const result = await authService.register(email, password, name);
          // No auto-login: user must verify email first.
          return { email: result.email };
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
          });
          throw error;
        }
      },

      verifyEmail: async (token: string) => {
        set({ isLoading: true, error: null });
        try {
          const tokens = await authService.verifyEmail(token);
          const user = await authService.getMe(tokens.accessToken);
          syncLanguage(user);
          set({
            tokens,
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Verification failed',
            isLoading: false,
          });
          throw error;
        }
      },

      resendVerification: async (email: string) => {
        await authService.resendVerification(email);
      },

      requestMagicLink: async (email: string) => {
        await authService.requestMagicLink(email);
      },

      magicLogin: async (token: string) => {
        set({ isLoading: true, error: null });
        try {
          const tokens = await authService.magicLogin(token);
          const user = await authService.getMe(tokens.accessToken);
          syncLanguage(user);
          set({
            tokens,
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Magic login failed',
            isLoading: false,
          });
          throw error;
        }
      },

      logout: async () => {
        const { tokens } = get();
        if (tokens) {
          try {
            await authService.logout(tokens.accessToken, tokens.refreshToken);
          } catch {
            // Ignore logout errors
          }
        }
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
        });
        // Clear the playbook cache so a new user session starts fresh (#719).
        usePlaybookStore.getState().resetPlaybooks();
      },

      checkAuth: () => {
        if (checkAuthInFlight) return checkAuthInFlight;

        checkAuthInFlight = (async () => {
          const { tokens, user: storedUser } = get();

          if (!tokens) {
            set({ isLoading: false, isAuthenticated: false });
            return;
          }

          // Tokens + cached user exist in storage: trust them immediately so the
          // user never sees a login redirect on a simple page reload. getMe runs
          // below to refresh stale user data; if the access token has expired,
          // getMe 401s and the catch leg rotates the refresh token — which is a
          // foreground network round-trip, but the UI never shows the login screen
          // during it because isAuthenticated was already set above.
          if (storedUser) {
            syncLanguage(storedUser);
            set({ isAuthenticated: true, isLoading: false });
          }

          try {
            const user = await authService.getMe(tokens.accessToken);
            syncLanguage(user);
            set({ user, isAuthenticated: true, isLoading: false });
          } catch {
            // Try refreshing the token
            try {
              const newTokens = await authService.refreshToken(tokens.refreshToken);
              const user = await authService.getMe(newTokens.accessToken);
              syncLanguage(user);
              set({
                tokens: newTokens,
                user,
                isAuthenticated: true,
                isLoading: false,
              });
            } catch {
              set({
                user: null,
                tokens: null,
                isAuthenticated: false,
                isLoading: false,
              });
            }
          }
        })().finally(() => {
          checkAuthInFlight = null;
        });

        return checkAuthInFlight;
      },

      clearError: () => set({ error: null }),

      updateUser: (userData: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...userData } });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        tokens: state.tokens,
        user: state.user,
      }),
    }
  )
);
